/**
 * Storage adapter for cache persistence using IndexedDB with fallbacks
 * Handles data persistence across page refreshes and browser sessions
 */

import { CacheEntry, STORAGE_CONFIG, DEBUG_CONFIG } from '@/stores/cacheConfig';

/**
 * Storage adapter interface
 */
export interface StorageAdapter {
  init(): Promise<void>;
  get(key: string): Promise<CacheEntry | null>;
  set(key: string, entry: CacheEntry): Promise<void>;
  delete(key: string): Promise<void>;
  clear(pattern?: string): Promise<void>;
  getAllKeys(): Promise<string[]>;
  getSize(): Promise<number>;
  cleanup(): Promise<void>;
}

/**
 * IndexedDB storage adapter
 */
class IndexedDBAdapter implements StorageAdapter {
  private db: IDBDatabase | null = null;
  private dbName = STORAGE_CONFIG.dbName;
  private storeName = STORAGE_CONFIG.storeName;
  private version = STORAGE_CONFIG.dbVersion;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        if (DEBUG_CONFIG.enableDebug) {
          console.log('IndexedDB initialized successfully');
        }
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('expiry', 'expiry', { unique: false });
          store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
      };
    });
  }

  async get(key: string): Promise<CacheEntry | null> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const entry = request.result;
        if (entry && entry.expiry > Date.now()) {
          // Update access time
          entry.accessCount = (entry.accessCount || 0) + 1;
          entry.lastAccessed = Date.now();
          this.updateAccessTime(key, entry);
          resolve(entry);
        } else if (entry) {
          // Expired entry, remove it
          this.delete(key);
          resolve(null);
        } else {
          resolve(null);
        }
      };
    });
  }

  async set(key: string, entry: CacheEntry): Promise<void> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Calculate entry size
      entry.size = new Blob([JSON.stringify(entry.value)]).size;
      entry.accessCount = entry.accessCount || 0;
      entry.lastAccessed = entry.timestamp;
      
      const request = store.put(entry);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async delete(key: string): Promise<void> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear(pattern?: string): Promise<void> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    if (!pattern) {
      // Clear all
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    }

    // Clear by pattern
    const keys = await this.getAllKeys();
    const keysToDelete = keys.filter(key => key.includes(pattern));
    
    await Promise.all(keysToDelete.map(key => this.delete(key)));
  }

  async getAllKeys(): Promise<string[]> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAllKeys();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result as string[]);
    });
  }

  async getSize(): Promise<number> {
    if (!this.db) throw new Error('IndexedDB not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const entries = request.result as CacheEntry[];
        const totalSize = entries.reduce((sum, entry) => sum + (entry.size || 0), 0);
        resolve(totalSize);
      };
    });
  }

  async cleanup(): Promise<void> {
    if (!this.db) return;

    const now = Date.now();
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('expiry');
    
    // Get all expired entries
    const range = IDBKeyRange.upperBound(now);
    const request = index.openCursor(range);

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  }

  private async updateAccessTime(key: string, entry: CacheEntry): Promise<void> {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      store.put(entry);
    } catch (error) {
      // Ignore access time update errors
      if (DEBUG_CONFIG.enableDebug) {
        console.warn('Failed to update access time:', error);
      }
    }
  }
}

/**
 * Memory storage adapter (fallback)
 */
class MemoryAdapter implements StorageAdapter {
  private storage = new Map<string, CacheEntry>();

  async init(): Promise<void> {
    // Memory storage doesn't need initialization
    if (DEBUG_CONFIG.enableDebug) {
      console.log('Memory storage adapter initialized');
    }
  }

  async get(key: string): Promise<CacheEntry | null> {
    const entry = this.storage.get(key);
    if (entry && entry.expiry > Date.now()) {
      entry.accessCount = (entry.accessCount || 0) + 1;
      entry.lastAccessed = Date.now();
      return entry;
    } else if (entry) {
      this.storage.delete(key);
    }
    return null;
  }

  async set(key: string, entry: CacheEntry): Promise<void> {
    entry.size = new Blob([JSON.stringify(entry.value)]).size;
    entry.accessCount = entry.accessCount || 0;
    entry.lastAccessed = entry.timestamp;
    this.storage.set(key, entry);
  }

  async delete(key: string): Promise<void> {
    this.storage.delete(key);
  }

  async clear(pattern?: string): Promise<void> {
    if (!pattern) {
      this.storage.clear();
      return;
    }

    const keysToDelete = Array.from(this.storage.keys()).filter(key => 
      key.includes(pattern)
    );
    
    keysToDelete.forEach(key => this.storage.delete(key));
  }

  async getAllKeys(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }

  async getSize(): Promise<number> {
    let totalSize = 0;
    for (const entry of this.storage.values()) {
      totalSize += entry.size || 0;
    }
    return totalSize;
  }

  async cleanup(): Promise<void> {
    const now = Date.now();
    const expiredKeys = Array.from(this.storage.entries())
      .filter(([, entry]) => entry.expiry <= now)
      .map(([key]) => key);
    
    expiredKeys.forEach(key => this.storage.delete(key));
  }
}

/**
 * Create and initialize storage adapter
 */
export const createStorageAdapter = async (): Promise<StorageAdapter> => {
  let adapter: StorageAdapter;

  // Try IndexedDB first
  try {
    adapter = new IndexedDBAdapter();
    await adapter.init();
    
    if (DEBUG_CONFIG.enableDebug) {
      console.log('Using IndexedDB storage adapter');
    }
    
    return adapter;
  } catch (error) {
    if (DEBUG_CONFIG.enableDebug) {
      console.warn('IndexedDB failed, falling back to memory storage:', error);
    }
  }

  // Fallback to memory storage
  adapter = new MemoryAdapter();
  await adapter.init();
  
  return adapter;
};

/**
 * Utility to check storage availability
 */
export const checkStorageAvailability = (): {
  indexedDB: boolean;
  localStorage: boolean;
  sessionStorage: boolean;
} => {
  return {
    indexedDB: 'indexedDB' in window,
    localStorage: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
      } catch {
        return false;
      }
    })(),
    sessionStorage: (() => {
      try {
        sessionStorage.setItem('test', 'test');
        sessionStorage.removeItem('test');
        return true;
      } catch {
        return false;
      }
    })()
  };
};